import type { WhatsAppMessage } from "@/components/marketing";

export type CampaignChannelKind = "whatsapp" | "email" | "notification" | "sms";

export interface CampaignChannel {
  kind: CampaignChannelKind;
  tab: string;
  // Text panel beside the device.
  title: string;
  text: string;
  points: string[];
}

// "One campaign, every channel": the same Diwali offer from one shop, shown
// the way a customer sees it on WhatsApp, in their inbox, as a phone
// notification and as an SMS. The shop and figures are an
// illustration.
export const channelPreview = {
  heading: "See a campaign on every channel",
  intro:
    "One offer, written once. Pick a channel to see what your customer sees.",
  ariaLabel:
    "One Diwali campaign shown on WhatsApp, email, a phone notification and SMS.",
  tablistLabel: "Campaign channels",
  shop: {
    name: "Fydo Basket",
    initial: "F",
    handle: "fydobasket.store",
    email: "offers@fydobasket.in",
  },
  offer: {
    title: "Diwali Dhamaka",
    value: "Flat 15% off",
    detail: "On bills above ₹999 · 1 to 5 November",
    code: "DIWALI15",
    button: "Shop the offer",
  },
  whatsApp: {
    subtitle: "Business account",
    message: {
      text: "Happy Diwali, Priya! 🪔\nFlat **15% off** at Fydo Basket on bills above ₹999, from 1 to 5 November. Show this message at the counter.",
      time: "10:30 am",
      extra: { kind: "code", label: "Your offer code", value: "DIWALI15" },
      button: "Shop the offer",
    } satisfies WhatsAppMessage,
  },
  email: {
    subject: "🪔 Your Diwali gift: flat 15% off",
    preheader: "Members only, 1 to 5 November",
    greeting: "Hi Priya,",
    body: "Thank you for shopping with us this year. Here is a Diwali gift for you: flat 15% off on bills above ₹999.",
    footer:
      "You are receiving this because you shop with Fydo Basket. Unsubscribe",
  },
  notification: {
    time: "10:30",
    date: "Saturday, 1 November",
    app: "FYDO BASKET",
    when: "now",
    title: "Your Diwali gift is here 🪔",
    body: "Flat 15% off on bills above ₹999. Use code DIWALI15 at the counter.",
    second: {
      app: "FYDO BASKET",
      when: "2d ago",
      title: "₹120 is waiting in your wallet",
      body: "Use it on your next visit.",
    },
  },
  sms: {
    sender: "FYDOBK",
    senderInitials: "FB",
    when: "Text Message · Today 10:30",
    text: "Fydo Basket: Happy Diwali! Flat 15% off on bills above Rs 999, 1-5 Nov. Show this SMS at the counter.",
    link: "fydo.in/diwali",
  },
  channels: [
    {
      kind: "whatsapp",
      tab: "WhatsApp",
      title: "WhatsApp",
      text: "The channel your customers open first. Messages arrive in their chat with your shop's name.",
      points: [
        "Sent to the customer group you choose",
        "Carries an offer code or a wallet credit",
        "Schedule it for the best time of day",
      ],
    },
    {
      kind: "email",
      tab: "Email",
      title: "Email",
      text: "A branded offer in the inbox, with room for a picture, a story and a button.",
      points: [
        "Your logo, colours and offer",
        "Good for longer news and catalogues",
        "Customers can unsubscribe in one tap",
      ],
    },
    {
      kind: "notification",
      tab: "Notification",
      title: "App notification",
      text: "A short nudge on the lock screen of customers who use the Fydo app.",
      points: [
        "Seen without opening any app",
        "Best for reminders and wallet balances",
        "Keep it to one line and one offer",
      ],
    },
    {
      kind: "sms",
      tab: "SMS",
      title: "SMS",
      text: "A plain text message that reaches every phone, with or without internet or an app.",
      points: [
        "Works on any phone, even without data",
        "Keep it short: the offer, the dates and a link",
        "Sent from your shop's sender ID",
      ],
    },
  ] satisfies CampaignChannel[],
};
